import { useCallback, useState } from 'react';
import { useListConversation } from 'features/ai-assistant/hooks';
import { useShowAlert } from 'hooks';
import strings from 'strings';

export const useChatSession = ({ sessionID }) => {
  const [currentSessionId, setCurrentSessionId] = useState(sessionID || null);
  const [resetKey, setResetKey] = useState(0);

  const selectSession = useCallback(sessionId => {
    setCurrentSessionId(sessionId);
  }, []);

  const clearSession = useCallback(() => {
    setCurrentSessionId(null);
    setResetKey(prev => prev + 1);
  }, []);

  return {
    clearSession,
    currentSessionId,
    resetKey,
    selectSession,
    setSessionId: setCurrentSessionId
  };
};

export const useChatList = ({ apiInstance, endpoints } = {}) => {
  const {
    conversations: chats,
    deleteConversation,
    uiStatus,
    updateConversations,
    pinConversation,
    unpinConversation,
    renameConversation,
    fetchMoreConversations,
    hasMoreConversations,
    isLoadingMoreConversations
  } = useListConversation({ apiInstance, endpoints });
  const { showAlert } = useShowAlert();

  const handlePinConversation = useCallback(
    async chatId => {
      const chat = chats.find(chatItem => chatItem.sessionId === chatId);
      if (!chat) return;

      const previousChats = [...chats];
      const newIsPinned = !chat.isPinned;

      updateConversations(currentChats => {
        const currentChat = currentChats.find(chatItem => chatItem.sessionId === chatId);
        if (!currentChat) return currentChats;

        const updatedChat = { ...currentChat, isPinned: newIsPinned };
        const otherChats = currentChats.filter(chatItem => chatItem.sessionId !== chatId);

        const pinnedChats = otherChats.filter(chatItem => chatItem.isPinned);
        const unpinnedChats = otherChats.filter(chatItem => !chatItem.isPinned);

        if (newIsPinned) {
          return [...pinnedChats, updatedChat, ...unpinnedChats];
        }
        return [...pinnedChats, updatedChat, ...unpinnedChats];
      });

      const success = newIsPinned ? await pinConversation(chatId) : await unpinConversation(chatId);

      if (!success) {
        updateConversations(previousChats);
        showAlert({
          severity: 'error',
          body: strings.formatString(strings.ai.generators.assistant.errors.failedAction, {
            action: newIsPinned ? 'pin' : 'unpin'
          })
        });
      }
    },
    [chats, pinConversation, unpinConversation, updateConversations, showAlert]
  );

  const handleRenameConversation = useCallback(
    async (chatId, newTitle) => {
      const chat = chats.find(chatItem => chatItem.sessionId === chatId);
      if (!chat || chat.sessionTitle === newTitle) return;

      const previousChats = [...chats];

      updateConversations(currentChats => {
        const currentChat = currentChats.find(chatItem => chatItem.sessionId === chatId);
        if (!currentChat) return currentChats;

        const updatedChat = { ...currentChat, sessionTitle: newTitle };

        if (updatedChat.isPinned) {
          return currentChats.map(chatItem =>
            chatItem.sessionId === chatId ? updatedChat : chatItem
          );
        }

        const otherChats = currentChats.filter(chatItem => chatItem.sessionId !== chatId);
        const pinnedChats = otherChats.filter(chatItem => chatItem.isPinned);
        const unpinnedChats = otherChats.filter(chatItem => !chatItem.isPinned);

        return [...pinnedChats, updatedChat, ...unpinnedChats];
      });

      const success = await renameConversation(chatId, newTitle);

      if (!success) {
        updateConversations(previousChats);
        showAlert({
          severity: 'error',
          body: strings.formatString(strings.ai.generators.assistant.errors.failedAction, {
            action: 'rename'
          })
        });
      }
    },
    [chats, renameConversation, updateConversations, showAlert]
  );

  const handleDeleteConversation = useCallback(
    async chatId => {
      const chat = chats.find(chatItem => chatItem.sessionId === chatId);
      if (!chat) return false;

      const previousChats = [...chats];
      updateConversations(currentChats =>
        currentChats.filter(chatItem => chatItem.sessionId !== chatId)
      );

      const success = await deleteConversation(chatId);

      if (!success) {
        updateConversations(previousChats);
        showAlert({
          severity: 'error',
          body: strings.formatString(strings.ai.generators.assistant.errors.failedAction, {
            action: 'delete'
          })
        });
      }

      return success;
    },
    [chats, deleteConversation, updateConversations, showAlert]
  );

  return {
    chats,
    fetchMoreConversations,
    handleDeleteConversation,
    handlePinConversation,
    handleRenameConversation,
    hasMoreConversations,
    isLoadingMoreConversations,
    uiStatus,
    updateConversations
  };
};

export const useAIAssistantDrawer = ({
  apiInstance,
  endpoints,
  sessionID = null,
  onDelete = null
} = {}) => {
  const { currentSessionId, resetKey, selectSession, clearSession, setSessionId } = useChatSession({
    sessionID
  });
  const {
    chats,
    fetchMoreConversations,
    handleDeleteConversation: handleDeleteConversationBase,
    handlePinConversation,
    handleRenameConversation,
    hasMoreConversations,
    isLoadingMoreConversations,
    uiStatus,
    updateConversations
  } = useChatList({ apiInstance, endpoints });

  const handleMessageReplied = useCallback(
    responseData => {
      if (responseData && responseData.sessionId && responseData.sessionTitle) {
        updateConversations(prevChats => {
          const existingChat = prevChats.find(chat => chat.sessionId === responseData.sessionId);

          if (existingChat) {
            const otherChats = prevChats.filter(
              chatItem => chatItem.sessionId !== responseData.sessionId
            );
            const pinnedChats = otherChats.filter(chatItem => chatItem.isPinned);
            const unpinnedChats = otherChats.filter(chatItem => !chatItem.isPinned);

            if (existingChat.isPinned) {
              return [existingChat, ...pinnedChats, ...unpinnedChats];
            }
            return [...pinnedChats, existingChat, ...unpinnedChats];
          }

          const pinnedChats = prevChats.filter(chatItem => chatItem.isPinned);
          const unpinnedChats = prevChats.filter(chatItem => !chatItem.isPinned);
          const newChat = {
            sessionId: responseData.sessionId,
            sessionTitle: responseData.sessionTitle,
            isPinned: false
          };

          return [...pinnedChats, newChat, ...unpinnedChats];
        });
        setSessionId(responseData.sessionId);
      }
    },
    [updateConversations, setSessionId]
  );

  const handleDeleteConversation = useCallback(
    async chatId => {
      const wasActiveSession = currentSessionId === chatId;
      if (wasActiveSession) {
        clearSession();
      }
      const success = await handleDeleteConversationBase(chatId);

      if (success && onDelete) onDelete(chatId);

      if (!success && wasActiveSession) {
        setSessionId(prevSessionId =>
          prevSessionId == null || prevSessionId === chatId ? chatId : prevSessionId
        );
      }

      return success;
    },
    [handleDeleteConversationBase, currentSessionId, clearSession, setSessionId, onDelete]
  );

  return {
    chats,
    currentSessionId,
    fetchMoreConversations,
    handleClickConversation: selectSession,
    handleClickNewChat: clearSession,
    handleDeleteConversation,
    handleMessageReplied,
    handlePinConversation,
    handleRenameConversation,
    hasMoreConversations,
    isLoadingMoreConversations,
    resetKey,
    uiStatus
  };
};
